import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DefaultValuesService } from '../../services/defaultvalues.service';
import { NavItem } from './navitem';
import { isClusterVisible } from '../../config/cluster-visibility.config';
import { filter } from 'rxjs/operators';

const API_VERSION = "api/generic/version";

@Component({
    selector: 'sidenav-menu',
    templateUrl: 'sidenav.component.html',
    styleUrls: ['sidenav.component.less', '../../style/generic.component.less'],
    standalone: false
})
export class SidenavComponent implements OnInit {
    isExpanded = false;
    currentUrl: string = '';
    expandedClusterId: number | null = null;
    private pendingUrlResolve = true;
    version: string | undefined = '000.00';

    constructor(public defaults: DefaultValuesService,
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly cdr: ChangeDetectorRef,
        @Inject('BASE_URL') private readonly baseUrl: string
    ) {
        const url = this.baseUrl + API_VERSION;

        this.http.get<string>(url).subscribe({
            next: (result) => {
                this.version = result;
            },
            error: (error) => console.error(error)
        });

        const saved = localStorage.getItem('currentSelectedItem');
        if (saved !== null) {
            this.expandedClusterId = Number(saved);
        }
    }

    ngOnInit(): void {
        this.currentUrl = this.router.url.split('?')[0];

        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe((event) => {
            this.currentUrl = event.urlAfterRedirects.split('?')[0];
            this.pendingUrlResolve = true;
        });
    }

    isClusterExpanded(item: NavItem): boolean {
        if (this.pendingUrlResolve && this.defaults.menuClusters?.length) {
            this.pendingUrlResolve = false;
            if (this.currentUrl && this.currentUrl !== '/' && this.currentUrl !== '/home') {
                const matched = this.defaults.menuClusters.find(c => this.matchesCluster(c, this.currentUrl));
                if (matched !== undefined) {
                    this.expandedClusterId = matched.ClusterId ?? null;
                    if (this.expandedClusterId !== null) {
                        localStorage.setItem('currentSelectedItem', this.expandedClusterId.toString());
                    }
                }
            } else {
                this.expandedClusterId = null;
            }
        }
        return item.ClusterId === this.expandedClusterId;
    }

    onClusterClosed(item: NavItem): void {
        if (item.ClusterId === this.expandedClusterId) {
            this.expandedClusterId = null;
            this.cdr.detectChanges();
            this.expandedClusterId = item.ClusterId;
            this.cdr.detectChanges();
        }
    }

    onClusterOpened(item: NavItem): void {
        if (item.ClusterId === undefined) return;
        if (item.ClusterId !== this.expandedClusterId) {
            this.expandedClusterId = item.ClusterId;
            this.pendingUrlResolve = false; // prevent URL resolution from overriding manual selection
            localStorage.setItem('currentSelectedItem', item.ClusterId.toString());
        }
    }

    private matchesCluster(item: NavItem, url: string): boolean {
        if (item.Route) {
            const route = item.Route.startsWith('/') ? item.Route : '/' + item.Route;
            if (this.matchesRoute(route, url)) return true;
        }
        if (item.ClusterComponents && item.ClusterComponents.length > 0) {
            return item.ClusterComponents.some(child => {
                if (!child.Route || !child.Enabled) return false;
                const route: string = child.Route.startsWith('/') ? child.Route : '/' + child.Route;
                return this.matchesRoute(route, url);
            });
        }
        return false;
    }

    private matchesRoute(route: string, url: string): boolean {
        return url === route || url.startsWith(route + '/') || url.startsWith(route + '#');
    }

    isExternalLink(link) {
        const httpRegex = /^http:\/\//i; // Match "http://" with case-insensitivity
        const httpsRegex = /^https:\/\//i; // Match "https://" with case-insensitivity
        return httpRegex.test(link) || httpsRegex.test(link);
    }
    externalRedirect(link) {
        window.open(link, "_blank");
    }

    isClusterVisible(item: any): boolean {
        const name = item.DisplayName || '';
        return isClusterVisible(this.defaults, name);
    }

    navigateToClusterLanding(item: NavItem, event: Event): void {
        // Use the Route from backend data
        if (item.Route) {
            event.stopPropagation();
            const route = item.Route.startsWith('/') ? item.Route : '/' + item.Route;
            this.router.navigate([route]);
        }
        // If no Route, do nothing — let the sidenav expand normally
    }
}
